import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbActiveModal, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { format, isValid, parse } from 'date-fns';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { FlatpickrDatePickerComponent } from '../../../../../../_shared/components/flatpickr-date-picker/flatpickr-date-picker.component';
import { ErrorHandlerService } from '../../../../../../_shared/services/error-handler.service';
import {
  COMPLIANCE_CALENDAR_EVENT_TYPE_LABEL_KEYS,
  ComplianceCalendarEventScheduleResponse,
  CompliancePhase,
  ComplianceTaskType,
  UpdateComplianceCalendarEventScheduleApiRequest
} from '../../../models/compliance-item.model';
import { ComplianceOverviewService } from '../../../services/compliance-overview.service';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { translateComplianceCatalogueName } from '../../../../_shared/helpers/compliance-lookup-label.helper';

const SCHEDULED_DATE_INPUT_FORMAT = 'yyyy/MM/dd';
const SCHEDULED_DATE_DISPLAY_FORMAT = 'EEEE, dd MMM yyyy';

@Component({
  selector: 'app-compliance-event-schedule-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, NgbModule, FlatpickrDatePickerComponent, TranslocoDirective, TranslocoPipe],
  templateUrl: './compliance-event-schedule-modal.component.html',
  styleUrl: './compliance-event-schedule-modal.component.scss'
})
export class ComplianceEventScheduleModalComponent implements OnDestroy {
  private _eventData?: ComplianceCalendarEventScheduleResponse;

  @Input() sourceTaskId!: number;
  @Input() taskType!: ComplianceTaskType;
  @Input() phase: CompliancePhase | null = null;
  @Input()
  set eventData(value: ComplianceCalendarEventScheduleResponse | undefined) {
    this._eventData = value;
    if (value) {
      this.populateForm();
    }
  }

  get eventData(): ComplianceCalendarEventScheduleResponse | undefined {
    return this._eventData;
  }

  /** Label shown in the modal header for routine tasks; empty string for periodic. */
  get phaseLabel(): string {
    const calendarEventType = this.eventData?.calendarEventType;
    if (!calendarEventType || calendarEventType === 'periodic' || calendarEventType === 'unknown') {
      return '';
    }

    const labelKey = COMPLIANCE_CALENDAR_EVENT_TYPE_LABEL_KEYS[calendarEventType];
    return labelKey ? this.transloco.translate(labelKey) : '';
  }

  get scheduledDateValue(): string {
    return this.form.get('scheduledDate')?.value ?? '';
  }

  get scheduledDateDisplayValue(): string {
    const parsed = this.parseScheduledDate(this.scheduledDateValue);
    return parsed ? format(parsed, SCHEDULED_DATE_DISPLAY_FORMAT) : '';
  }

  get hasChanges(): boolean {
    return this.scheduledDateValue !== this.initialScheduledDateValue;
  }

  form: FormGroup;
  isLoading = false;
  isEditingDetails = false;

  private initialScheduledDateValue = '';
  private originalScheduledDate?: Date;
  private readonly cancelSubject = new Subject<void>();

  private readonly complianceOverviewService = inject(ComplianceOverviewService);
  private readonly errorHandlerService = inject(ErrorHandlerService);
  private readonly toastr = inject(ToastrService);
  private readonly transloco = inject(TranslocoService);
  private readonly activeModal = inject(NgbActiveModal);
  private readonly fb = inject(FormBuilder);

  constructor() {
    this.form = this.fb.group({
      assetName: [{ value: '', disabled: true }],
      make: [{ value: '', disabled: true }],
      model: [{ value: '', disabled: true }],
      complianceStandard: [{ value: '', disabled: true }],
      checklistType: [{ value: '', disabled: true }],
      workshop: [{ value: '', disabled: true }],
      scheduledDate: [null as string | null, Validators.required]
    });
  }

  ngOnDestroy(): void {
    this.cancelSubject.next();
    this.cancelSubject.complete();
  }

  toggleEditDetails(): void {
    this.isEditingDetails = !this.isEditingDetails;
    if (!this.isEditingDetails) {
      this.populateForm();
    }
  }

  closeModal(): void {
    this.activeModal.close();
  }

  onCancel(): void {
    this.activeModal.dismiss();
  }

  onScheduledDateChange(value: string | undefined): void {
    const nextValue = value?.trim() ?? '';
    if (nextValue === this.scheduledDateValue) {
      return;
    }

    const control = this.form.get('scheduledDate');
    control?.setValue(nextValue);
    control?.markAsTouched();
  }

  async onSubmit(): Promise<void> {
    if (this.form.invalid || !this.eventData || !this.sourceTaskId || !this.taskType) {
      this.form.markAllAsTouched();
      return;
    }

    const scheduledDate = this.mergeScheduledDateWithOriginalTime(this.scheduledDateValue);
    if (!scheduledDate) {
      return;
    }

    this.isLoading = true;

    try {
      this.cancelSubject.next();

      const request: UpdateComplianceCalendarEventScheduleApiRequest = {
        taskType: this.taskType,
        sourceTaskId: this.sourceTaskId,
        scheduledDate,
        phase: this.phase
      };

      await this.complianceOverviewService.updateComplianceCalendarEventScheduleAsync(request, this.cancelSubject);
      this.toastr.success(this.transloco.translate('compliance.overview.eventScheduleModal.toasts.updated'));
      this.isLoading = false;
      this.activeModal.close(true);
    } catch (error) {
      this.errorHandlerService.handleError(error, {
        showToast: true,
        callback: () => {
          this.isLoading = false;
        }
      });
    }
  }

  private populateForm(): void {
    if (!this.eventData) {
      return;
    }

    const original = new Date(this.eventData.scheduledDate);
    this.originalScheduledDate = isValid(original) ? original : undefined;
    const scheduledDateValue = this.originalScheduledDate
      ? format(this.originalScheduledDate, SCHEDULED_DATE_INPUT_FORMAT)
      : '';
    this.initialScheduledDateValue = scheduledDateValue;

    this.form.patchValue({
      assetName: this.eventData.assetName || '',
      make: this.eventData.make || '',
      model: this.eventData.model || '',
      complianceStandard: this.eventData.complianceStandard || '',
      // Read-only display; the raw name is never sent back from this form.
      checklistType: translateComplianceCatalogueName(this.transloco, this.eventData.checklistType),
      workshop: this.eventData.workshop || '',
      scheduledDate: scheduledDateValue
    });

    this.isEditingDetails = false;
  }

  private parseScheduledDate(value: string): Date | null {
    if (!value) {
      return null;
    }

    const parsed = parse(value, SCHEDULED_DATE_INPUT_FORMAT, new Date());
    return isValid(parsed) ? parsed : null;
  }

  private mergeScheduledDateWithOriginalTime(value: string): Date | null {
    const selectedDate = this.parseScheduledDate(value);
    if (!selectedDate) {
      return null;
    }

    if (!this.originalScheduledDate) {
      return selectedDate;
    }

    selectedDate.setHours(
      this.originalScheduledDate.getHours(),
      this.originalScheduledDate.getMinutes(),
      this.originalScheduledDate.getSeconds(),
      this.originalScheduledDate.getMilliseconds()
    );

    return selectedDate;
  }
}